import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useState, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import dayjs from 'dayjs';
import { CREATE_EXPENSE_REPORT_OPTIONS } from './constants';
import { $t, useTranslation } from '../../../hooks/useTranslation';
export const CreateExpenseReportContext = createContext({});
// Helper function to create date field validation schema
const createDateFieldSchema = (fieldName, isRequired = true) => {
    const baseSchema = z.instanceof(dayjs, {
        message: $t(`${fieldName} is required`)
    });
    return isRequired
        ? baseSchema.refine((value) => !Number.isNaN(value.date()), {
            message: $t(`Invalid ${fieldName}`)
        })
        : baseSchema.optional().refine((value) => !value || !Number.isNaN(value.date()), {
            message: $t(`Invalid ${fieldName}`)
        });
};
// Helper function to apply date range refinements to a schema
const withDateRangeValidation = (schema) => {
    return schema
        .refine((data) => data.startDate.isSameOrBefore(data.endDate), {
        message: $t('Start Date must be on or before End Date'),
        path: ['startDate']
    })
        .refine((data) => data.endDate.isSameOrAfter(data.startDate), {
        message: $t('End Date must be on or after Start Date'),
        path: ['endDate']
    });
};
// Helper function to create base date range schema
const createBaseDateRangeSchema = () => {
    return z.object({
        startDate: createDateFieldSchema('Start Date'),
        endDate: createDateFieldSchema('End Date')
    });
};
// Helper function to create card programs schema
const createCardProgramsSchema = () => {
    return z.array(z.any()).nonempty($t('At least 1 card needs to be selected'));
};
// Helper function to create UDF schema fields
const createUdfFields = () => ({
    udf1: z.optional(z.number()),
    udf2: z.optional(z.number()),
    udf3: z.optional(z.number()),
    udf4: z.optional(z.number()),
    udf5: z.optional(z.number())
});
export function CreateExpenseReportProvider({ children }) {
    useTranslation();
    const [stepNumber, setStepNumber] = useState(1);
    const [selectedOptionValue, setSelectedOptionValue] = useState(null);
    const [alertType, setAlertType] = useState(null);
    const [expenseReportCreationOptions, setExpenseReportCreationOptions] = useState();
    const [expenseReportCreationViewModel, setExpenseReportCreationViewModel] = useState();
    const [expenseReportPreview, setExpenseReportPreview] = useState({
        ExpectedExpenseCount: 0,
        ExpectedReceiptCount: 0
    });
    const [skippedToBlankReport, setSkippedToBlankReport] = useState(false);
    const getSchemaForStep1Option = (option) => {
        switch (option) {
            case CREATE_EXPENSE_REPORT_OPTIONS.BLANK:
            case CREATE_EXPENSE_REPORT_OPTIONS.ALL_ITEMS:
                return z.object({});
            case CREATE_EXPENSE_REPORT_OPTIONS.CREDIT_CARD_TRANSACTIONS:
                return withDateRangeValidation(createBaseDateRangeSchema().extend({
                    cardPrograms: createCardProgramsSchema()
                }));
            case CREATE_EXPENSE_REPORT_OPTIONS.DATE_RANGE:
                return withDateRangeValidation(createBaseDateRangeSchema());
            default:
                return z.object({});
        }
    };
    const getSchemaForStep2 = () => {
        return withDateRangeValidation(z.object({
            reportName: z.string().min(1, { message: $t('Report Name is required') }),
            ...createBaseDateRangeSchema().shape,
            description: z.string().refine((val) => val.length <= 250, {
                message: $t('Description has a max length of 250 characters')
            }),
            ...createUdfFields(),
            billable: z.optional(z.boolean()),
            linkToSpendRequestId: z.optional(z.number()),
            cardPrograms: z.optional(z.array(z.any()))
        }));
    };
    const formSchema = useMemo(() => {
        if (stepNumber === 1 && selectedOptionValue !== null) {
            return getSchemaForStep1Option(selectedOptionValue);
        }
        if (stepNumber === 2) {
            return getSchemaForStep2();
        }
        // Default schema for step 1 when no option is selected
        return z.object({});
    }, [selectedOptionValue, stepNumber]);
    const form = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: {
            reportName: '',
            billable: false,
            startDate: undefined,
            endDate: undefined,
            description: '',
            udf1: undefined,
            udf2: undefined,
            udf3: undefined,
            udf4: undefined,
            udf5: undefined,
            linkToSpendRequestId: undefined,
            cardPrograms: undefined
        }
    });
    const contextValue = useMemo(() => ({
        stepNumber,
        setStepNumber,
        selectedOptionValue,
        setSelectedOptionValue,
        alertType,
        setAlertType,
        expenseReportCreationOptions,
        setExpenseReportCreationOptions,
        expenseReportPreview,
        setExpenseReportPreview,
        setExpenseReportCreationViewModel,
        expenseReportCreationViewModel,
        skippedToBlankReport,
        setSkippedToBlankReport
    }), [
        stepNumber,
        selectedOptionValue,
        alertType,
        expenseReportCreationOptions,
        expenseReportPreview,
        expenseReportCreationViewModel,
        skippedToBlankReport
    ]);
    return (_jsx(CreateExpenseReportContext.Provider, { value: contextValue, children: _jsx(FormProvider, { ...form, children: children }) }));
}
